import React, { Component } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { IFilter } from "@formant/core/model/IFilter";
import { Fader } from "@formant/ui/component/common/Fader";

import hintConfigureSvg from "../../static/images/hint-configure.svg";
import hintEditLayoutSvg from "../../static/images/hint-edit-layout.svg";
import hintTeleoperationSvg from "../../static/images/hint-teleoperation.svg";
import { routerStore } from "../../stores";
import { hashObservable } from "../../utils/hashObservable";
import { localSettingObservable } from "../../utils/localSettingObservable";
import { IconButton } from "../common/IconButton";
import { StandardButton } from "../common/StandardButton";
import { TextButton } from "../common/TextButton";
import * as styles from "./HelpHints.scss";

export interface IHelpHintsProps {
    context: IFilter;
}

@observer
export class HelpHints extends Component<IHelpHintsProps> {
    @hashObservable()
    private showHelp: boolean = false;
    @localSettingObservable("observe.hide-help")
    private hideHelp: boolean = false;
    @observable
    private active: number = 1;

    public render() {
        if (!this.showHelp) {
            return false;
        }

        const { context } = this.props;
        const { routeTo } = routerStore;

        const [deviceId] =
            context.deviceIds?.length === 1 ? context.deviceIds : [];

        return (
            <Fader
                visible={!this.hideHelp}
                className={classnames(
                    styles["help-hints"],
                    styles[`hint-${this.active}`],
                )}
                visibleClassName={styles.visible}
            >
                <div className={styles.box}>
                    <div className={styles.hint}>
                        <div className={styles.graphic}>
                            <img src={hintConfigureSvg} />
                        </div>
                        <div className={styles.content}>
                            <h4>
                                We selected streams that might be interesting to
                                you
                            </h4>
                            <p>
                                If you don't see the data that you are
                                interested in, you can add or remove the
                                telemetry data streams by{" "}
                                <TextButton
                                    to={routeTo.editDevice(
                                        {
                                            id: deviceId,
                                        },
                                        { tab: "Telemetry" },
                                    )}
                                >
                                    configuring this device
                                </TextButton>
                                . See{" "}
                                <TextButton to="https://docs.formant.io/docs">
                                    Formant docs
                                </TextButton>{" "}
                                for more.
                            </p>
                        </div>
                        <StandardButton
                            onClicked={this.onNext}
                            className={styles.button}
                        >
                            Next
                        </StandardButton>
                    </div>
                    <div className={styles.hint}>
                        <div className={styles.graphic}>
                            <img src={hintTeleoperationSvg} />
                        </div>
                        <div className={styles.content}>
                            <h4>Control this device with Teleoperate</h4>
                            <p>
                                Once configured you can control this device to
                                remotely using a peer-to-peer, real-time
                                connection. Let’s get you{" "}
                                <TextButton
                                    to={routeTo.editDevice(
                                        { id: deviceId },
                                        { tab: "Teleoperation" },
                                    )}
                                >
                                    set up now
                                </TextButton>
                                .
                            </p>
                        </div>
                        <StandardButton
                            onClicked={this.onNext}
                            className={styles.button}
                        >
                            Next
                        </StandardButton>
                    </div>
                    <div className={styles.hint}>
                        <div className={styles.graphic}>
                            <img src={hintEditLayoutSvg} />
                        </div>
                        <div className={styles.content}>
                            <h4>Customize this View</h4>
                            <p>
                                View is a way to group visualization modules
                                within the device channel. It defines the group
                                of streams and its layout. You can{" "}
                                <TextButton to={routeTo.createView()}>
                                    customize it
                                </TextButton>{" "}
                                or{" "}
                                <TextButton to={routeTo.createView()}>
                                    add a new view
                                </TextButton>
                                .
                            </p>
                        </div>
                        <StandardButton
                            onClicked={this.onNext}
                            className={styles.button}
                        >
                            Next
                        </StandardButton>
                    </div>
                    <IconButton
                        icon="close"
                        className={styles.close}
                        onClicked={this.onClose}
                    />
                </div>
            </Fader>
        );
    }

    private onNext = () => {
        this.active = (this.active % 3) + 1;
    };

    private onClose = () => {
        this.hideHelp = true;
    };
}
